// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import color from "@hardwoods/XDL/Utilities/color";
import { iconButtonCommonStyles, iconLinkCommonStyles } from "@hardwoods/XDL/Utilities/iconButtonCommonStyles";
import baseTheme from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { resolveColor, safeColor } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import Color from "color";
import * as React from "react";
import { css } from "styled-components";

export const styledScrollbar = (width = 6) => {
    return ({ theme }: any) => css`
        scrollbar-width: thin;
        scrollbar-color: ${resolveColor("common.ui12", theme)} ${resolveColor("common.ui10", theme)};

        &::-webkit-scrollbar {
            width: ${width}px;
            height: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: ${resolveColor("common.ui10", theme)};
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${resolveColor("common.ui12", theme)};
            border-radius: 6px;
            border: 3px solid transparent;
        }
        &::-webkit-scrollbar-thumb:hover {
            background: ${(props: any) => {
                const hoverColor = resolveColor("common.ui12", props.theme);
                return Color(safeColor(hoverColor))["darken"](0.3).toString();
            }};
        }
    `;
};

export const xGap = (gap: string) => {
    const checkIfZero = (value: string) => (value === "0" ? `${value}px` : value);
    const addUnit = (value: string) => (value === `${parseFloat(value)}` ? `${value}px` : value);
    const gapArgs = gap.split(" ");
    let rowGap = checkIfZero(gapArgs[0]);
    let columnGap = gapArgs.length > 1 ? checkIfZero(gapArgs[1]) : checkIfZero(gapArgs[0]);
    rowGap = addUnit(rowGap);
    columnGap = addUnit(columnGap);
    return css`
        display: flex;
        flex-wrap: wrap;
        --column-gap: ${columnGap};
        --row-gap: ${rowGap};
        margin: calc(var(--row-gap) * -1) 0 0 calc(var(--column-gap) * -1);
        width: calc(100% + var(--column-gap));

        & > * {
            margin: var(--row-gap) 0 0 var(--column-gap);
        }
    `;
};

// hide Increase/Decrease arrow for TextField with number type
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [20] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

// Add a line on the right side of the title
export const lineOnTitleRightSide = css`
    position: relative;
    overflow: hidden;

    &:after {
        content: "";
        width: 100%;
        height: 1px;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        margin-left: 15px;
        background-color: ${color("common.border")};
    }
`;

export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
            margin-right: 0;
        }
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h2",
    as: "h1",
    css: css`
        margin: 0 0 30px 0;
    `,
};

export { iconButtonCommonStyles, iconLinkCommonStyles };
